import {
  FileState,
  FileNode,
  NodesMap,
  ActionType,
  UploadProgress,
} from "./types";

export type FileAction =
  | { type: "SET_NODES"; payload: NodesMap }
  | { type: "ADD_NODE"; payload: FileNode }
  | { type: "UPDATE_NODE"; payload: { id: string; updates: Partial<FileNode> } }
  | { type: "REMOVE_NODE"; payload: string }
  | { type: "REMOVE_NODES"; payload: string[] }
  | { type: "SET_ACTIVE_FOLDER"; payload: string | null }
  | { type: "SET_VIEW_MODE"; payload: "grid" | "list" }
  | {
      type: "SET_SORT";
      payload: { key: "name" | "updatedAt" | "size"; dir: "asc" | "desc" };
    }
  | { type: "SELECT_NODE"; payload: string }
  | { type: "DESELECT_NODE"; payload: string }
  | { type: "SET_SELECTED"; payload: string[] }
  | { type: "CLEAR_SELECTION" }
  | { type: "TOGGLE_EXPAND"; payload: string }
  | { type: "SET_EXPANDED"; payload: { id: string; expanded: boolean } }
  | { type: "SET_LOADING"; payload: boolean }
  | { type: "SET_UPLOAD_PROGRESS"; payload: UploadProgress }
  | { type: "REMOVE_UPLOAD"; payload: string }
  | {
      type: "SHOW_CONTEXT_MENU";
      payload: { x: number; y: number; nodeId?: string };
    }
  | { type: "HIDE_CONTEXT_MENU" }
  // Optimistic operations
  | { type: "OP_START"; payload: { opId: string; actionType: ActionType } }
  | { type: "OP_SUCCESS"; payload: { opId: string } }
  | {
      type: "OP_FAILURE";
      payload: { opId: string; error: string; snapshot: Partial<FileState> };
    }
  // Optimistic move
  | {
      type: "MOVE_OPTIMISTIC";
      payload: { nodeIds: string[]; targetParentId: string | null };
    }
  | { type: "MOVE_SUCCESS"; payload: { nodes: FileNode[] } }
  // Optimistic rename
  | { type: "RENAME_OPTIMISTIC"; payload: { nodeId: string; newName: string } }
  | { type: "RENAME_SUCCESS"; payload: { node: FileNode } }
  // Optimistic delete
  | { type: "DELETE_OPTIMISTIC"; payload: { nodeIds: string[] } }
  | { type: "DELETE_SUCCESS"; payload: { nodeIds: string[] } }
  // Optimistic create folder
  | { type: "CREATE_FOLDER_OPTIMISTIC"; payload: { tempNode: FileNode } }
  | {
      type: "CREATE_FOLDER_SUCCESS";
      payload: { tempId: string; node: FileNode };
    };

export const initialState: FileState = {
  nodes: {},
  rootIds: [],
  activeFolderId: null,
  viewMode: "list",
  sortBy: { key: "name", dir: "asc" },
  selectedIds: new Set(),
  expandedFolderIds: new Set(),
  loading: false,
  uploads: {},
  operations: {},
  contextMenu: null,
};

export function fileReducer(state: FileState, action: FileAction): FileState {
  switch (action.type) {
    case "SET_NODES": {
      return {
        ...state,
        nodes: action.payload,
        rootIds: Object.values(action.payload)
          .filter((node) => node.parentId === null)
          .map((node) => node.id),
      };
    }

    case "ADD_NODE": {
      const node = action.payload;
      const newNodes = { ...state.nodes, [node.id]: node };
      const newRootIds =
        node.parentId === null && !state.rootIds.includes(node.id)
          ? [...state.rootIds, node.id]
          : state.rootIds;

      return {
        ...state,
        nodes: newNodes,
        rootIds: newRootIds,
      };
    }

    case "UPDATE_NODE": {
      const { id, updates } = action.payload;
      if (!state.nodes[id]) return state;

      const oldNode = state.nodes[id];
      const newNode = { ...oldNode, ...updates };

      // Handle parent change (move operation)
      let newRootIds = state.rootIds;
      if (
        updates.parentId !== undefined &&
        updates.parentId !== oldNode.parentId
      ) {
        // Removing from root
        if (oldNode.parentId === null && updates.parentId !== null) {
          newRootIds = state.rootIds.filter((rootId) => rootId !== id);
        }
        // Adding to root
        if (oldNode.parentId !== null && updates.parentId === null) {
          newRootIds = [...state.rootIds, id];
        }
      }

      return {
        ...state,
        nodes: { ...state.nodes, [id]: newNode },
        rootIds: newRootIds,
      };
    }

    case "REMOVE_NODE": {
      const { [action.payload]: removed, ...remainingNodes } = state.nodes;
      return {
        ...state,
        nodes: remainingNodes,
        rootIds: state.rootIds.filter((id) => id !== action.payload),
        selectedIds: new Set(
          [...state.selectedIds].filter((id) => id !== action.payload)
        ),
      };
    }

    case "REMOVE_NODES": {
      const idsToRemove = new Set(action.payload);
      const newNodes = Object.fromEntries(
        Object.entries(state.nodes).filter(([id]) => !idsToRemove.has(id))
      );
      return {
        ...state,
        nodes: newNodes,
        rootIds: state.rootIds.filter((id) => !idsToRemove.has(id)),
        selectedIds: new Set(
          [...state.selectedIds].filter((id) => !idsToRemove.has(id))
        ),
      };
    }

    case "SET_ACTIVE_FOLDER": {
      return {
        ...state,
        activeFolderId: action.payload,
        selectedIds: new Set(), // Clear selection when changing folders
      };
    }

    case "SET_VIEW_MODE": {
      return {
        ...state,
        viewMode: action.payload,
      };
    }

    case "SET_SORT": {
      return {
        ...state,
        sortBy: action.payload,
      };
    }

    case "SELECT_NODE": {
      return {
        ...state,
        selectedIds: new Set([...state.selectedIds, action.payload]),
      };
    }

    case "DESELECT_NODE": {
      const newSelected = new Set(state.selectedIds);
      newSelected.delete(action.payload);
      return {
        ...state,
        selectedIds: newSelected,
      };
    }

    case "SET_SELECTED": {
      return {
        ...state,
        selectedIds: new Set(action.payload),
      };
    }

    case "CLEAR_SELECTION": {
      return {
        ...state,
        selectedIds: new Set(),
      };
    }

    case "TOGGLE_EXPAND": {
      const newExpanded = new Set(state.expandedFolderIds);
      if (newExpanded.has(action.payload)) {
        newExpanded.delete(action.payload);
      } else {
        newExpanded.add(action.payload);
      }
      return {
        ...state,
        expandedFolderIds: newExpanded,
      };
    }

    case "SET_EXPANDED": {
      const newExpanded = new Set(state.expandedFolderIds);
      if (action.payload.expanded) {
        newExpanded.add(action.payload.id);
      } else {
        newExpanded.delete(action.payload.id);
      }
      return {
        ...state,
        expandedFolderIds: newExpanded,
      };
    }

    case "SET_LOADING": {
      return {
        ...state,
        loading: action.payload,
      };
    }

    case "SET_UPLOAD_PROGRESS": {
      return {
        ...state,
        uploads: {
          ...state.uploads,
          [action.payload.fileId]: action.payload,
        },
      };
    }

    case "REMOVE_UPLOAD": {
      const { [action.payload]: removed, ...remainingUploads } = state.uploads;
      return {
        ...state,
        uploads: remainingUploads,
      };
    }

    case "SHOW_CONTEXT_MENU": {
      return {
        ...state,
        contextMenu: {
          visible: true,
          x: action.payload.x,
          y: action.payload.y,
          nodeId: action.payload.nodeId,
        },
      };
    }

    case "HIDE_CONTEXT_MENU": {
      return {
        ...state,
        contextMenu: null,
      };
    }

    // Optimistic operations
    case "OP_START": {
      return {
        ...state,
        operations: {
          ...state.operations,
          [action.payload.opId]: {
            id: action.payload.opId,
            type: action.payload.actionType,
            status: "pending",
          },
        },
      };
    }

    case "OP_SUCCESS": {
      const { [action.payload.opId]: completed, ...remainingOps } =
        state.operations;
      return {
        ...state,
        operations: remainingOps,
      };
    }

    case "OP_FAILURE": {
      const { snapshot } = action.payload;
      const { [action.payload.opId]: failed, ...remainingOps } =
        state.operations;

      // Rollback by applying snapshot
      return {
        ...state,
        ...snapshot,
        operations: remainingOps,
      };
    }

    // Move operations
    case "MOVE_OPTIMISTIC": {
      const { nodeIds, targetParentId } = action.payload;
      const newNodes = { ...state.nodes };
      const now = new Date().toISOString();

      nodeIds.forEach((id) => {
        if (newNodes[id]) {
          newNodes[id] = {
            ...newNodes[id],
            parentId: targetParentId,
            updatedAt: now,
          };
        }
      });

      // Update rootIds if moving to/from root
      let newRootIds = [...state.rootIds];
      nodeIds.forEach((id) => {
        const oldNode = state.nodes[id];
        if (!oldNode) return;

        // Moved from root to folder
        if (oldNode.parentId === null && targetParentId !== null) {
          newRootIds = newRootIds.filter((rootId) => rootId !== id);
        }
        // Moved from folder to root
        if (oldNode.parentId !== null && targetParentId === null) {
          if (!newRootIds.includes(id)) {
            newRootIds.push(id);
          }
        }
      });

      return {
        ...state,
        nodes: newNodes,
        rootIds: newRootIds,
      };
    }

    case "MOVE_SUCCESS": {
      // Update nodes with confirmed data from server
      const newNodes = { ...state.nodes };
      action.payload.nodes.forEach((node) => {
        if (newNodes[node.id]) {
          newNodes[node.id] = { ...newNodes[node.id], ...node };
        }
      });
      return {
        ...state,
        nodes: newNodes,
      };
    }

    // Rename operations
    case "RENAME_OPTIMISTIC": {
      const { nodeId, newName } = action.payload;
      if (!state.nodes[nodeId]) return state;

      return {
        ...state,
        nodes: {
          ...state.nodes,
          [nodeId]: {
            ...state.nodes[nodeId],
            name: newName,
            updatedAt: new Date().toISOString(),
          },
        },
      };
    }

    case "RENAME_SUCCESS": {
      const { node } = action.payload;
      if (!state.nodes[node.id]) return state;

      return {
        ...state,
        nodes: {
          ...state.nodes,
          [node.id]: { ...state.nodes[node.id], ...node },
        },
      };
    }

    // Delete operations
    case "DELETE_OPTIMISTIC": {
      const { nodeIds } = action.payload;
      const idsToRemove = new Set(nodeIds);
      const newNodes = Object.fromEntries(
        Object.entries(state.nodes).filter(([id]) => !idsToRemove.has(id))
      );

      return {
        ...state,
        nodes: newNodes,
        rootIds: state.rootIds.filter((id) => !idsToRemove.has(id)),
        selectedIds: new Set(
          [...state.selectedIds].filter((id) => !idsToRemove.has(id))
        ),
      };
    }

    case "DELETE_SUCCESS": {
      // Already removed optimistically, nothing to do
      return state;
    }

    // Create folder operations
    case "CREATE_FOLDER_OPTIMISTIC": {
      const { tempNode } = action.payload;
      const newNodes = { ...state.nodes, [tempNode.id]: tempNode };
      const newRootIds =
        tempNode.parentId === null
          ? [...state.rootIds, tempNode.id]
          : state.rootIds;

      return {
        ...state,
        nodes: newNodes,
        rootIds: newRootIds,
      };
    }

    case "CREATE_FOLDER_SUCCESS": {
      const { tempId, node } = action.payload;
      // Replace temp node with real node from server
      const { [tempId]: removed, ...remainingNodes } = state.nodes;
      const newNodes = { ...remainingNodes, [node.id]: node };

      // Update rootIds
      let newRootIds = state.rootIds;
      if (node.parentId === null) {
        newRootIds = state.rootIds.map((id) => (id === tempId ? node.id : id));
      }

      return {
        ...state,
        nodes: newNodes,
        rootIds: newRootIds,
      };
    }

    default:
      return state;
  }
}
